import { useState } from 'react';
import {
  makeStyles, shorthands, tokens, Text, Switch, Input, Button,
  MessageBar, MessageBarBody, Popover, PopoverTrigger, PopoverSurface,
} from '@fluentui/react-components';
import { useApi } from '../api/context';
import type { Framework } from '../api/client';

const useStyles = makeStyles({
  body: {
    display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS,
    minWidth: '340px', ...shorthands.padding(tokens.spacingVerticalS),
  },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  row: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS },
});

/**
 * Records whether a commercial licence is held for a framework's content.
 *
 * Taken at the operator's word. There is no way for an application to verify a
 * licence agreement and pretending otherwise would be theatre. What it does
 * instead is attribute the claim: who stated it, when, and optionally an
 * agreement reference. A licence claim with nobody's name against it is an
 * assumption rather than a claim.
 */
export function LicenceToggle({ framework, onChanged }: {
  framework: Framework;
  onChanged: () => void;
}) {
  const styles = useStyles();
  const api = useApi();
  const [held, setHeld] = useState(framework.commercialLicenceAsserted);
  const [reference, setReference] = useState(framework.licenceReference ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (next: boolean) => {
    setBusy(true); setError(null);
    try {
      await api.assertLicence(framework.frameworkId, next, reference.trim() || null);
      setHeld(next);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
      setHeld(framework.commercialLicenceAsserted);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger disableButtonEnhancement>
        <Button size="small" appearance={held ? 'primary' : 'secondary'}>
          {held ? 'Licensed' : 'No licence'}
        </Button>
      </PopoverTrigger>

      <PopoverSurface>
        <div className={styles.body}>
          <Text weight="semibold">Commercial licence</Text>
          <Text className={styles.muted}>
            {framework.name} is published under {framework.licenceCode.toUpperCase()}.
            Some uses need a commercial licence from the publisher.
          </Text>

          <div className={styles.row}>
            <Switch
              checked={held}
              disabled={busy}
              onChange={(_, d) => void save(d.checked)}
              label={held ? 'We hold a commercial licence' : 'We do not hold one'}
            />
          </div>

          <Input
            value={reference}
            disabled={busy}
            placeholder="Agreement or membership reference, optional"
            onChange={(_, d) => setReference(d.value)}
            onBlur={() => held && void save(true)}
          />

          <Text className={styles.muted}>
            This is taken at your word and not verified. It is recorded with your name
            and the date, so the claim is attributable.
          </Text>

          {held && framework.licenceAssertedBy && (
            <Text className={styles.muted}>
              Asserted by {framework.licenceAssertedBy}
              {framework.licenceAssertedUtc
                ? ` on ${framework.licenceAssertedUtc.slice(0, 10)}`
                : ''}.
            </Text>
          )}

          {error && (
            <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
}
